import {
  type IndicativeRange,
  type LmiCostInput,
  LmiCostInputSchema,
  type LmiCostOutput,
  type LmiCostRuleSet,
  type LvrTier,
  LMI_WAIVED,
  type PayNowVsSaveInput,
  PayNowVsSaveInputSchema,
  type PayNowVsSaveOutput,
  type RefundInput,
  RefundInputSchema,
  type RefundOnDischargeOutput,
  type RefundOnDischargeRuleSet,
  type RefundTier,
  type SaveTo20Scenario,
} from "./types.js";

// ---------------------------------------------------------------------------
// LMI calc surfaces. Pure: inputs + rulesets always yield the same output; no
// I/O, no Date.now() (the period is a parameter). Zod validates at the boundary
// so malformed payloads fail loudly. Every output passes `assertNoCreditHandoff`
// before it leaves — the C2 NCCP kill-line is enforced in code, not just prose.
// ---------------------------------------------------------------------------

/**
 * Hard architectural rule (panel lens 2, made binding): the downstream mortgage
 * is NCCP-regulated credit. A personalised LMI / break-even / refund result
 * routed to a paid broker/lender/loan handoff is dealing-by-arranging — the C2
 * kill-line. This guard runs over every result's serialised form and throws if
 * a credit/broker/loan-product identifier leaks in. No affiliate, lender or
 * broker reference may ever reach a personalised surface. Tested in
 * calculate.test.ts.
 */
const FORBIDDEN_HANDOFF_TERMS = [
  "broker",
  "apply now",
  "get a loan",
  "home loan offer",
  "compare loans",
  "compare lenders",
  "pre-approval",
  "preapproval",
  "refinance now",
  "best rate",
  "lowest rate",
  "we recommend",
  "you should",
  "save $",
];

export function assertNoCreditHandoff(output: unknown): void {
  const serialised = JSON.stringify(output).toLowerCase();
  for (const term of FORBIDDEN_HANDOFF_TERMS) {
    if (serialised.includes(term)) {
      throw new Error(
        `LMI output leaked a forbidden credit/handoff/advice term (NCCP C2): "${term}"`,
      );
    }
  }
}

const round = (n: number) => Math.round(n);

/** Pick the LVR tier whose `[fromLvr, toLvr)` contains `lvr`. */
function tierForLvr(tiers: readonly LvrTier[], lvr: number): LvrTier {
  for (const tier of tiers) {
    if (lvr >= tier.fromLvr && (tier.toLvr === null || lvr < tier.toLvr)) {
      return tier;
    }
  }
  throw new Error(`no LMI LVR tier matched LVR ${lvr}`);
}

// ===========================================================================
// lmiCost — INDICATIVE premium range. Never a quote.
// ===========================================================================

export function calculateLmiCost(
  rawInput: LmiCostInput,
  rules: LmiCostRuleSet,
): LmiCostOutput {
  const input = LmiCostInputSchema.parse(rawInput);

  const loanAmount =
    input.loanAmount ?? input.propertyValue - (input.deposit as number);
  if (loanAmount <= 0) {
    // Loan fully covered by deposit → no LMI.
    const out: LmiCostOutput = {
      lvr: 0,
      loanAmount: Math.max(0, loanAmount),
      lmiApplies: false,
      waivedByFirstHomeGuarantee: false,
      indicativePremium: { low: LMI_WAIVED, high: LMI_WAIVED },
      lvrBand: "Below 80% LVR",
      nextTierDown: null,
      isIndicativeEstimate: true,
    };
    assertNoCreditHandoff(out);
    return out;
  }

  const lvr = loanAmount / input.propertyValue;
  const tier = tierForLvr(rules.tiers, lvr);
  const lmiApplies = lvr > rules.lmiThresholdLvr && !input.firstHomeGuaranteeEligible;

  // FHBG waiver zeroes the premium (the tool's differentiator) — even above 80%.
  const waived = input.firstHomeGuaranteeEligible && lvr > rules.lmiThresholdLvr;

  const indicativePremium: IndicativeRange =
    lmiApplies
      ? {
          low: round(loanAmount * tier.ratePctLow),
          high: round(loanAmount * tier.ratePctHigh),
        }
      : { low: LMI_WAIVED, high: LMI_WAIVED };

  // "One tier down": the floor of the current tier is the LVR at which the
  // cheaper tier begins; extra deposit to reach it drops you under that cliff.
  const idx = rules.tiers.indexOf(tier);
  const nextTierDown =
    lmiApplies && idx > 0
      ? {
          atLvr: tier.fromLvr,
          // Deposit at LVR = fromLvr is propertyValue × (1 − fromLvr); extra
          // over today's deposit (propertyValue − loanAmount).
          extraDeposit: round(
            input.propertyValue * (1 - tier.fromLvr) -
              (input.propertyValue - loanAmount),
          ),
        }
      : null;

  // When LMI doesn't apply (at/below the 80% threshold, FHBG waiver aside) the
  // band label is the no-LMI band, not the tier that arithmetically contains a
  // boundary LVR. The 80% threshold itself is a no-LMI point.
  const lvrBand =
    !lmiApplies && !waived && lvr <= rules.lmiThresholdLvr
      ? "Below 80% LVR"
      : tier.label;

  const out: LmiCostOutput = {
    lvr,
    loanAmount,
    lmiApplies,
    waivedByFirstHomeGuarantee: waived,
    indicativePremium,
    lvrBand,
    nextTierDown,
    isIndicativeEstimate: true,
  };
  assertNoCreditHandoff(out);
  return out;
}

// ===========================================================================
// payNowVsSaveTo20 — wait-vs-now as a SENSITIVITY BAND. Never "you're ahead $X".
// ===========================================================================

/** Years for a growing deposit to reach 20% of a target that itself grows. */
function yearsToReach20(
  propertyValue: number,
  deposit: number,
  annualSavingsRate: number,
  priceMovement: number,
  maxYears = 60,
): { years: number | null; value: number | null } {
  // Already there.
  if (deposit >= propertyValue * 0.2) {
    return { years: 0, value: propertyValue };
  }
  // Without any saving capacity the target can never be caught.
  if (annualSavingsRate <= 0) return { years: null, value: null };

  let value = propertyValue;
  let dep = deposit;
  for (let y = 1; y <= maxYears; y++) {
    value = value * (1 + priceMovement);
    dep = dep + annualSavingsRate;
    if (dep >= value * 0.2) {
      return { years: y, value: round(value) };
    }
  }
  return { years: null, value: null };
}

export function calculatePayNowVsSaveTo20(
  rawInput: PayNowVsSaveInput,
  costRules: LmiCostRuleSet,
): PayNowVsSaveOutput {
  const input = PayNowVsSaveInputSchema.parse(rawInput);

  const loanNow = input.propertyValue - input.deposit;
  const lvrNow = loanNow > 0 ? loanNow / input.propertyValue : 0;
  const alreadyAt20 = input.deposit >= input.propertyValue * 0.2;

  // Indicative LMI if buying now — reuse the cost surface (range, never quote).
  const costNow = calculateLmiCost(
    {
      propertyValue: input.propertyValue,
      loanAmount: Math.max(loanNow, 0.000001),
      assessmentYear: input.assessmentYear,
    },
    costRules,
  );

  const scenarios: SaveTo20Scenario[] = input.priceMovementScenarios.map(
    (priceMovementAssumption) => {
      const { years, value } = yearsToReach20(
        input.propertyValue,
        input.deposit,
        input.annualSavingsRate,
        priceMovementAssumption,
      );
      return {
        priceMovementAssumption,
        yearsToReach20: years,
        projectedPropertyValue: value,
      };
    },
  );

  const out: PayNowVsSaveOutput = {
    lvrNow,
    indicativePremiumNow: alreadyAt20
      ? { low: 0, high: 0 }
      : costNow.indicativePremium,
    alreadyAt20,
    scenarios,
    isSensitivityBand: true,
  };
  assertNoCreditHandoff(out);
  return out;
}

// ===========================================================================
// refundOnDischarge — DISQUALIFIER-LED. Never "you're owed $X".
// ===========================================================================

function refundTierFor(
  tiers: readonly RefundTier[],
  months: number,
): RefundTier | null {
  for (const tier of tiers) {
    if (months >= tier.fromMonths && (tier.toMonths === null || months < tier.toMonths)) {
      return tier;
    }
  }
  return null;
}

export function calculateRefundOnDischarge(
  rawInput: RefundInput,
  rules: RefundOnDischargeRuleSet,
): RefundOnDischargeOutput {
  const input = RefundInputSchema.parse(rawInput);

  const disqualifiers: string[] = [];
  const insurerDiscontinuedRefunds = rules.refundsDiscontinuedInsurers.includes(
    input.insurer,
  );

  // LEAD with the insurer-specific disqualifier — this is the core decay fact.
  if (insurerDiscontinuedRefunds) {
    disqualifiers.push(
      `Your insurer no longer offers LMI refunds, so no refund is payable regardless of how early you discharge.`,
    );
  }
  if (input.insurer === "unknown") {
    disqualifiers.push(
      `We can't confirm any refund without knowing your LMI insurer — refund policy varies by insurer and several have withdrawn refunds. Check with your lender.`,
    );
  }
  if (!input.fullyDischarged) {
    disqualifiers.push(
      `A refund requires that all loans secured by the property are repaid in full and the mortgage fully discharged. A partial discharge does not qualify.`,
    );
  }
  if (!input.lenderNotifiedWithinWindow) {
    disqualifiers.push(
      `A refund requires the lender to notify the insurer within about ${rules.lenderNoticeWindowDays} days of discharge. Missing that window forfeits the refund.`,
    );
  }

  const tier = refundTierFor(rules.tiers, input.monthsSincePremium);
  if (tier === null || (tier.refundPctLow === 0 && tier.refundPctHigh === 0)) {
    disqualifiers.push(
      `Refunds typically apply only when the loan is discharged within about 24 months of the premium. Beyond that, no refund is generally available.`,
    );
  }

  const potentiallyEligible = disqualifiers.length === 0 && tier !== null;

  // ONLY when every gate passes do we produce an INDICATIVE range — and even
  // then it is "possible, confirm with your lender", never "you are owed $X".
  let indicativeRefundRange: IndicativeRange | null = null;
  let tierLabel: string | null = null;
  if (potentiallyEligible && tier) {
    const afterStampLow = tier.refundPctLow - rules.stampDutyDeductionPctIndicative;
    const afterStampHigh = tier.refundPctHigh - rules.stampDutyDeductionPctIndicative;
    indicativeRefundRange = {
      low: Math.max(0, round(input.premiumPaid * afterStampLow)),
      high: Math.max(0, round(input.premiumPaid * afterStampHigh)),
    };
    tierLabel = tier.label;
  }

  const out: RefundOnDischargeOutput = {
    disqualifiers,
    potentiallyEligible,
    insurerDiscontinuedRefunds,
    indicativeRefundRange,
    tierLabel,
    isIndicativePossibleRefund: true,
  };
  assertNoCreditHandoff(out);
  return out;
}
